import { useState, useCallback, useContext, createContext, FC, useEffect } from 'react';
import { UserType } from '../lib/types'
import { doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { useFirestore, useSigninCheck, useAuth } from './use-firebase';
import { SubscriptionTypeEnum } from '../lib/enums';

const UserContext = createContext({
  user: {} as UserType | undefined,
  subscription: SubscriptionTypeEnum.Free,
});

// A custom hook for global state management
export const useUserContext = () => {
  const user = useContext(UserContext);
  if (!user) {
    throw new Error("useUserContext must be used within a UserContextProvider");
  }
  return user;
};

type UserContextProviderProps = {
  children: React.ReactNode;
}

// The provider for your state
export const UserContextProvider: FC<UserContextProviderProps> = ({ children }) => {
  // State managed with Immer
  const db = useFirestore()

  const { data: signInData } = useSigninCheck()
  const [user, setUser] = useState<UserType>();
  const { currentUser } = useAuth()
  const [sub, setSubscription] = useState(SubscriptionTypeEnum.Free)

  const getSubscription = useCallback(async () => {
    const result = await currentUser?.getIdTokenResult(true)
    const tier = result?.claims?.subscription

    if (!tier) {
      return SubscriptionTypeEnum.Free
    }

    return tier as SubscriptionTypeEnum
  }, [currentUser])

  useEffect(() => {
    const fetchSubscription = async () => {
      if (currentUser) {
        try {
          const sub = await getSubscription()
          setSubscription(sub || SubscriptionTypeEnum.Free)
        } catch (error) {
          console.error('Error fetching subscription:', error)
          setSubscription(SubscriptionTypeEnum.Free)
        }
      }
    }


    fetchSubscription()
  }, [currentUser, getSubscription])

  useEffect(() => {
    if (signInData?.signedIn) {
      const unsub = onSnapshot(doc(db, `users/${signInData?.user?.uid}`), (snapshot) => {
        const data = snapshot.data()
        const user = {
          id: snapshot.id,
          firstName: data?.firstName || "",
          lastName: data?.lastName || "",
          createAt: data?.createAt,
          email: data?.email,
          emailVerified: currentUser?.emailVerified || false
        }

        getSubscription()

        setUser((prev) => {
          if (prev) {
            return { ...prev, user }
          } else {
            return prev
          }
        })

      })
      return () => unsub();
    } else {
      // Clear state when user logs out
      setUser(undefined)
      setSubscription(SubscriptionTypeEnum.Free)
    }

  }, [signInData, currentUser?.emailVerified])

  return (
    <UserContext.Provider value={{ user, subscription: sub }}>
      {children}
    </UserContext.Provider>
  );
};
